import React, { useEffect, useState, useCallback } from 'react';
import { api, fmtMoney, formatApiError } from '../../lib/api';
import { useDarkMode } from '../../context/DarkModeContext';
import { Plus, ExternalLink, Copy, Trash2, RefreshCw, CreditCard, Receipt } from 'lucide-react';

const STATUS_PILL = (status, paymentStatus) => {
  if (paymentStatus === 'paid') return <span className="tag-pill ok">Paid</span>;
  if (status === 'expired' || status === 'failed') return <span className="tag-pill err">{status}</span>;
  if (status === 'initiated' || paymentStatus === 'pending') return <span className="tag-pill warn">Pending</span>;
  return <span className="tag-pill muted">{status || '—'}</span>;
};

const Payments = ({ lob }) => {
  const darkMode = useDarkMode();
  const aBg = darkMode ? lob?.accentBgDark : lob?.accentBg;
  const aTc = darkMode ? lob?.accentTcDark : lob?.accentTc;
  const [transactions, setTransactions] = useState([]);
  const [deals, setDeals] = useState([]);
  const [err, setErr] = useState('');
  const [showLink, setShowLink] = useState(false);
  const [showManual, setShowManual] = useState(false);

  const load = useCallback(async () => {
    if (!lob) return;
    try {
      const [t, d] = await Promise.all([
        api.get('/payments').then((r) => r.data),
        api.get(`/deals?lob_id=${lob.id}`).then((r) => r.data),
      ]);
      const dealIds = new Set(d.map((x) => x.id));
      setDeals(d);
      setTransactions(t.filter((tx) => !tx.deal_id || dealIds.has(tx.deal_id)));
    } catch (e) { setErr(formatApiError(e)); }
  }, [lob?.id]);

  // After redirect from Stripe, ?session_id={id} is appended — poll status.
  useEffect(() => {
    const url = new URL(window.location.href);
    const sid = url.searchParams.get('session_id');
    if (!sid) return;
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/checkout/status/${sid}`);
        if (data.payment_status === 'paid' || data.status === 'expired' || attempts >= 8) {
          url.searchParams.delete('session_id');
          window.history.replaceState({}, '', url.pathname);
          load();
          return;
        }
      } catch {}
      setTimeout(tick, 2500);
    };
    tick();
  }, [load]);

  useEffect(() => { load(); }, [load]);

  const refreshOne = async (tx) => {
    if (!tx.session_id) return;
    try { await api.get(`/payments/checkout/status/${tx.session_id}`); load(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  const del = async (tx) => {
    if (!window.confirm('Delete this manual payment?')) return;
    try { await api.delete(`/payments/${tx.id}`); load(); }
    catch (e) { alert(formatApiError(e)); }
  };

  const dealLabel = (id) => deals.find((d) => d.id === id)?.name || '—';

  return (
    <>
      <div className="page-toolbar" data-testid="payments-toolbar">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span className="page-title">Payments</span>
          <span className="lob-badge" style={{ background: aBg }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: lob.accent, display: 'inline-block', marginRight: 4 }} />
            <span style={{ color: aTc }}>{lob.label}</span>
          </span>
        </div>
        <button className="cta-btn" style={{ background: lob.accent }} onClick={() => setShowLink(true)} data-testid="create-link-btn">
          <CreditCard size={11} style={{ marginRight: 4 }} />Stripe payment link
        </button>
        <button className="cta-btn" style={{ background: 'var(--text-sub)' }} onClick={() => setShowManual(true)} data-testid="record-manual-btn">
          <Receipt size={11} style={{ marginRight: 4 }} />Record manual payment
        </button>
      </div>

      {err && <div className="module-banner err" data-testid="payments-error">{err}</div>}

      <div className="content-area">
        {transactions.length === 0 ? (
          <div className="empty-state" data-testid="payments-empty">
            <CreditCard size={36} style={{ opacity: 0.25 }} />
            <div className="ttl">No payments recorded</div>
            <div className="sub">Generate a Stripe checkout link or record a manual payment to get started.</div>
          </div>
        ) : (
          <div className="card">
            <table className="tbl">
              <thead><tr><th>Date</th><th>Deal</th><th>Type</th><th>Method</th><th>Amount</th><th>Status</th><th>Description</th><th></th></tr></thead>
              <tbody>
                {transactions.map((t) => (
                  <tr key={t.id} data-testid={`txn-row-${t.id}`}>
                    <td>{new Date(t.created_at).toLocaleDateString()}</td>
                    <td>{dealLabel(t.deal_id)}</td>
                    <td>{t.type === 'stripe' ? 'Stripe' : 'Manual'}</td>
                    <td>{t.method || '—'}</td>
                    <td><b>{fmtMoney(t.amount)}</b></td>
                    <td>{STATUS_PILL(t.status, t.payment_status)}</td>
                    <td>{t.description || t.reference || '—'}</td>
                    <td className="tbl-actions">
                      {t.link_url && (
                        <>
                          <a className="btn-link" href={t.link_url} target="_blank" rel="noreferrer" data-testid={`open-link-${t.id}`}><ExternalLink size={11} /></a>
                          <button className="btn-link" onClick={() => navigator.clipboard.writeText(t.link_url)} title="Copy link" data-testid={`copy-link-${t.id}`}><Copy size={11} /></button>
                          <button className="btn-link" onClick={() => refreshOne(t)} title="Refresh status" data-testid={`refresh-${t.id}`}><RefreshCw size={11} /></button>
                        </>
                      )}
                      {t.type === 'manual' && (
                        <button className="btn-link" style={{ color: 'var(--error)' }} onClick={() => del(t)} data-testid={`del-txn-${t.id}`}><Trash2 size={11} /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showLink && <StripeLinkModal deals={deals} onClose={(saved) => { setShowLink(false); if (saved) load(); }} />}
      {showManual && <ManualPaymentModal deals={deals} onClose={(saved) => { setShowManual(false); if (saved) load(); }} />}
    </>
  );
};

const StripeLinkModal = ({ deals, onClose }) => {
  const [form, setForm] = useState({ deal_id: deals[0]?.id || '', amount: '', description: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [created, setCreated] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr('');
    try {
      const { data } = await api.post('/payments/checkout/session', {
        deal_id: form.deal_id,
        amount: Number(form.amount),
        description: form.description || undefined,
        origin_url: window.location.origin,
      });
      setCreated(data);
    } catch (ex) { setErr(formatApiError(ex)); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay-v2" onClick={() => onClose(!!created)}>
      <form className="modal-card" onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="stripe-link-modal">
        <div className="modal-title">Generate Stripe payment link</div>
        {!created ? (
          <>
            <label className="f-label">Deal</label>
            <select className="f-select" value={form.deal_id} onChange={(e) => set('deal_id', e.target.value)} required data-testid="sl-deal">
              {deals.length === 0 ? <option value="">No deals available</option> : deals.map((d) => <option key={d.id} value={d.id}>{d.name} · {fmtMoney(d.amount)}</option>)}
            </select>
            <div className="modal-row">
              <div><label className="f-label">Amount (USD)</label><input className="f-input" type="number" step="0.01" min="0.5" required value={form.amount} onChange={(e) => set('amount', e.target.value)} data-testid="sl-amount" /></div>
              <div><label className="f-label">Description</label><input className="f-input" value={form.description} onChange={(e) => set('description', e.target.value)} data-testid="sl-description" /></div>
            </div>
            {err && <div className="f-error" data-testid="sl-error">{err}</div>}
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={() => onClose(false)}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={busy || !form.deal_id} data-testid="sl-submit">{busy ? 'Creating…' : 'Generate link'}</button>
            </div>
          </>
        ) : (
          <div data-testid="sl-success">
            <div style={{ fontSize: 12, color: 'var(--text-sub)', marginBottom: 10 }}>
              Payment link created. Send this URL to your customer:
            </div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
              <input className="f-input" readOnly value={created.link_url} onFocus={(e) => e.target.select()} data-testid="sl-link-url" />
              <button type="button" className="btn-primary" onClick={() => navigator.clipboard.writeText(created.link_url)}>Copy</button>
            </div>
            <a href={created.link_url} target="_blank" rel="noreferrer" className="btn-link" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <ExternalLink size={11} /> Open in new tab
            </a>
            <div className="modal-actions">
              <button type="button" className="btn-primary" onClick={() => onClose(true)}>Done</button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

const ManualPaymentModal = ({ deals, onClose }) => {
  const [form, setForm] = useState({ deal_id: deals[0]?.id || '', amount: '', method: 'check', reference: '', notes: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr('');
    try {
      await api.post('/payments/manual', { ...form, amount: Number(form.amount) });
      onClose(true);
    } catch (ex) { setErr(formatApiError(ex)); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay-v2" onClick={() => onClose(false)}>
      <form className="modal-card" onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="manual-payment-modal">
        <div className="modal-title">Record manual payment</div>
        <label className="f-label">Deal</label>
        <select className="f-select" value={form.deal_id} onChange={(e) => set('deal_id', e.target.value)} required data-testid="mp-deal">
          {deals.length === 0 ? <option value="">No deals available</option> : deals.map((d) => <option key={d.id} value={d.id}>{d.name} · {fmtMoney(d.amount)}</option>)}
        </select>
        <div className="modal-row">
          <div><label className="f-label">Amount (USD)</label><input className="f-input" type="number" step="0.01" min="0.01" required value={form.amount} onChange={(e) => set('amount', e.target.value)} data-testid="mp-amount" /></div>
          <div><label className="f-label">Method</label>
            <select className="f-select" value={form.method} onChange={(e) => set('method', e.target.value)} data-testid="mp-method">
              <option value="cash">Cash</option><option value="check">Check</option><option value="ach">ACH</option><option value="wire">Wire</option><option value="other">Other</option>
            </select>
          </div>
        </div>
        <label className="f-label">Reference</label>
        <input className="f-input" value={form.reference} onChange={(e) => set('reference', e.target.value)} placeholder="Check #, ACH trace, etc." data-testid="mp-reference" />
        <label className="f-label">Notes</label>
        <textarea className="f-textarea" rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} data-testid="mp-notes" />
        {err && <div className="f-error">{err}</div>}
        <div className="modal-actions">
          <button type="button" className="btn-secondary" onClick={() => onClose(false)}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={busy || !form.deal_id} data-testid="mp-submit">{busy ? 'Saving…' : 'Record payment'}</button>
        </div>
      </form>
    </div>
  );
};

export default Payments;
